// SIGNALSTODO - we can make this preview img on the chart definitions probably
import {
    ChartType,
    ISignalsChartDefinition,
    SignalDataEndpoint,
    SignalsEndpoints,
    SignalsObjectType,
    TableDataEndpoints,
} from "../../SignalsModels";
import BarChartImg from "./ChartPlaceholders/BarChart.png";
import DonutChartImg from "./ChartPlaceholders/DonutChart.png";
import EddyBarChartImg from "./ChartPlaceholders/EddyBarChart.png";
import EddyDoubleBarChartImg from "./ChartPlaceholders/EddyDoubleBarChart.png";
import EddyDoubleLineChartImg from "./ChartPlaceholders/EddyDoubleLineChart.png";
import EddyLineChartImg from "./ChartPlaceholders/EddyLineChart.png";
import EddyStackedBarChartImg from "./ChartPlaceholders/EddyStackedBarChart.png";
import HIPAALineChartImg from "./ChartPlaceholders/HIPAALineChart.png";
import HorizontalBarChartImg from "./ChartPlaceholders/HorizontalBarChart.png";
import LineChartImg from "./ChartPlaceholders/LineChart.png";
import MultiLineChartImg from "./ChartPlaceholders/MultiLineChart.png";
import StackedBarChartImg from "./ChartPlaceholders/StackedBarChart.png";
import TableChartImg from "./ChartPlaceholders/TableChart.png";
import TopicsOnTheRiseImg from "./ChartPlaceholders/TopicsOnTheRise.png";
import TreeMapChartImg from "./ChartPlaceholders/TreeMapChart.png";

const {
    eddyReports,
    trends,
    legacyTopicReports,
    themes,
    contacts,
    additionalReports,
    unique,
} = SignalsEndpoints;

const barChartDataEndpoints = [
    contacts.ContactTypePrevalenceId,
    additionalReports.JourneyStageCountId,
    additionalReports.CountByQuestionResponseId,
    additionalReports.ModelResultsCountId,
    additionalReports.TotalConversationsBarId,
] as SignalDataEndpoint[];

const eddyStackedBarChartDataEndpoints = [
    eddyReports.ClassifierAndEddyID,
    legacyTopicReports.ThemeAndEEID,
    themes.ThemePrevalenceAndEEId,
    contacts.ContactTypePrevalenceAndEEId,
    additionalReports.JourneyStageAndEddyEffectId,
] as SignalDataEndpoint[];

const doubleBarChartDataEndpoints = [
    eddyReports.AverageScoreByQuestionID,
] as SignalDataEndpoint[];
const horizontalBarDataEndpoints = [
    legacyTopicReports.PrimaryThemesId,
    themes.ThemePrevalenceId,
] as SignalDataEndpoint[];

const multiLineDataEndpoints = [
    eddyReports.EEByClassifiersOverTimeID,
    additionalReports.CountByQuestionResponseId,
    trends.ClassifierOverTimeId,
    trends.AverageScoreByModuleOverTimeID,
    trends.AverageScoreByQuestionOverTimeID,
    trends.SEPrevalenceOverTimeID,
    legacyTopicReports.ThemesOverTimeId,
    themes.ThemePrevalenceOverTimeId,
    contacts.ContactTypePrevalenceOverTimeId,
    contacts.EddyByContactTypeOverTimeId,
    eddyReports.EEPrevalenceByQuestionOverTimeID,
] as SignalDataEndpoint[];

const tableChartDataEndpoints = TableDataEndpoints;
const eddyLineChartDataEndpoints = [
    eddyReports.EEOverTimeID,
] as SignalDataEndpoint[];

const eddyBarChartDataEndpoints = [
    eddyReports.EEByClassifierID,
    eddyReports.EEPrevalenceByQuestionID,
    contacts.ContactTypePrevalenceId,
    eddyReports.AverageScoreByQuestionID,
] as SignalDataEndpoint[];

const eddyDoubleLineChartDataEndpoints = [
    eddyReports.AverageScoreByQuestionAndEddyOverTimeID,
] as SignalDataEndpoint[];

const singleLineChartDataEndpoints = [
    additionalReports.ConversationsOverTimeId,
    additionalReports.ModelResultsOverTimeId,
    trends.QualityScoreOverTimeID,
] as SignalDataEndpoint[];
const hipaaDataEndpoints = [
    trends.HipaaCompliancePrevalenceOverTimeId,
] as SignalDataEndpoint[];

const donutChartEndpoints = [
    additionalReports.CallDirectionId,
    additionalReports.AdverseEventsModelPerformanceId,
    additionalReports.SafetyEventsModelPerformanceId,
    additionalReports.ProductComplaintsModelPerformanceId,
    additionalReports.SpecialSituationsModelPerformanceId,
    additionalReports.TotalConversationsDonutId,
] as SignalDataEndpoint[];

export function getChartPreviewImage(
    chartDef: ISignalsChartDefinition,
): string {
    const endpoint = chartDef.dataEndpoint;

    if (chartDef.objectType === SignalsObjectType.Table) {
        return TableChartImg;
    }

    if (chartDef.typeOfChart === ChartType.TreeMap) {
        return TreeMapChartImg;
    }

    if (endpoint) {
        // Drivers on the Rise reuses the placeholder: it is the same four-tile layout, and the
        // image is a layout hint rather than a data preview.
        if (
            endpoint === legacyTopicReports.TopicsOnTheRiseID ||
            endpoint === unique.DriversOnTheRiseID
        ) {
            return TopicsOnTheRiseImg;
        }

        if (
            endpoint.includes(additionalReports.PublishedModelResultsCountId) ||
            barChartDataEndpoints.includes(endpoint)
        ) {
            return BarChartImg;
        }

        if (
            endpoint.includes(
                additionalReports.PublishedModelResultsOverTimeId,
            ) ||
            multiLineDataEndpoints.includes(endpoint)
        ) {
            return MultiLineChartImg;
        }

        if (eddyStackedBarChartDataEndpoints.includes(endpoint)) {
            return EddyStackedBarChartImg;
        }

        if (doubleBarChartDataEndpoints.includes(endpoint)) {
            return EddyDoubleBarChartImg;
        }

        if (horizontalBarDataEndpoints.includes(endpoint)) {
            return HorizontalBarChartImg;
        }

        if (tableChartDataEndpoints.includes(endpoint)) {
            return TableChartImg;
        }

        if (eddyBarChartDataEndpoints.includes(endpoint)) {
            return EddyBarChartImg;
        }

        if (eddyLineChartDataEndpoints.includes(endpoint)) {
            return EddyLineChartImg;
        }

        if (eddyDoubleLineChartDataEndpoints.includes(endpoint)) {
            return EddyDoubleLineChartImg;
        }

        if (singleLineChartDataEndpoints.includes(endpoint)) {
            return LineChartImg;
        }

        if (hipaaDataEndpoints.includes(endpoint)) {
            return HIPAALineChartImg;
        }

        if (donutChartEndpoints.includes(endpoint)) {
            return DonutChartImg;
        }
    }

    switch (chartDef.chartType) {
        case "Line":
            return LineChartImg;
        case "HorizontalBar":
            return HorizontalBarChartImg;
        case "StackedVerticalBar":
            return chartDef.isStackedBar
                ? StackedBarChartImg
                : EddyStackedBarChartImg;
        case "Table":
            return TableChartImg;
        case "Donut":
            return DonutChartImg;
        case "TreeMap":
            return TreeMapChartImg;
        case "VerticalBar":
            return BarChartImg;
        default:
            return chartDef.isStackedBar ? StackedBarChartImg : BarChartImg;
    }
}
